<script setup>
/**
 * FormControlMBasketOrdersLogs
 * Evaluates whether an order possesses system log histories.
 * If true, renders an ignition action button to spawn the log interaction modal.
 */
import { ref, watch, onMounted, getCurrentInstance } from 'vue';
import Button from "../Button.vue";

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const show = ref(false);

// --- Methods ---

/**
 * Checks the central store array of logged order indices
 * to see if this specific entity has entries.
 */
const evaluateLogStatus = async () => {
    const orderId = props.currentData?.id;

    if (!orderId) {
        show.value = false;
        props.field.hidden = true;
        return;
    }

    const loggedOrderIds = await store.dispatch('orderLogs/orderIds') || [];
    show.value = loggedOrderIds.includes(orderId);
};

/**
 * Triggers the operational store overlay system modal.
 */
const openLogsModal = () => {
    store.dispatch('modals/show', {
        componentName: 'OrdersLogsModal',
        obj: { orderId: props.currentData.id }
    });
};

// --- Watchers & Lifecycle ---

/**
 * Syncs the visibility configuration with the parent form group orchestrator.
 */
watch(show, (shouldShow) => {
    props.field.hidden = !shouldShow;
});

/**
 * Reactively monitors order record transitions (handles moving across rows or hitting /0)
 */
watch(() => props.currentData?.id, () => {
    evaluateLogStatus();
});

onMounted(() => {
    evaluateLogStatus();
});
</script>

<template>
    <div class="d-inline-block">
        <Button
            v-if="show"
            default-class="btn-light btn-sm border text-nowrap"
            @click.native="openLogsModal"
        >
            <i class="bi bi-journal-text me-1"></i> Open logs
        </Button>
    </div>
</template>